import React, { Key, useEffect, useRef, useState } from "react";

import {
  useSubmitVero1Checklist,
  useVero1ChecklistVersion,
} from "manager/hooks/api/vero1Queries";

import { Button } from "shared/components/Button";
import FlexContainer from "shared/components/FlexContainer";
import { Select } from "shared/components/Form/Select";
import Spacer from "shared/components/Spacer";
import { SmallText } from "shared/components/Typography";
import { stripPriorityPrefix } from "shared/components/vega/LumenV2/utils";
import type {
  Vero1Checklist,
  Vero1ChecklistItem,
  Vero1Severity,
} from "shared/interfaces/api/vero1";
import {
  CANCEL_REASON_LABELS,
  RECOMMENDATION_LABELS,
  resolveVero1RecommendationName,
  Vero1CancelReason,
  Vero1ChecklistStatus,
  Vero1ItemType,
  Vero1Recommendation,
} from "shared/interfaces/api/vero1";

import {
  RequiredMark,
  SelectField,
  SelectFieldMenuStyles,
  VERO1_DROPDOWN_CLASS,
} from "./editorStyled";
import {
  NO_VERDICT_LABEL,
  clientNameFor,
  displayName,
  effectiveTone,
  formatItemValue,
  isAnnotationOnlyItem,
  lumenLabel,
  severityLabel,
} from "./formatItemValue";
import { CircleAlertGlyph } from "./icons";
import {
  hasResultMoved,
  isAssessable,
  isAssessed,
  isIncomplete,
  isLeasingTouched,
  needsName,
  needsReason,
} from "./itemState";
import { codeOf } from "./persistItem";
import {
  disposeVero1ReportAutosaveQueue,
  getVero1ReportAutosaveQueue,
  REPORT_ENTITY,
} from "./reportAutosave";
import {
  LumenResultBadge,
  BackLink,
  CountChip,
  OptionCard,
  OptionDescription,
  OptionLabelHeading,
  OptionLabelPicker,
  OptionMain,
  OptionRadio,
  OptionRow,
  OptionTone,
  OptionWord,
  ReasonChip,
  ReasonChipRow,
  ReportCard,
  ReportCardBody,
  ReportCardHeader,
  ReportFooter,
  ReportFooterNote,
  SummaryCardFoot,
  SectionLabel,
  SubmitButton,
  SummaryAction,
  SummaryEntry,
  SummaryFieldLabel,
  SummaryFindings,
  SummaryResult,
  TextArea,
  TextInput,
  ConflictActionsBar,
  ConflictButtonRow,
  ConflictCopy,
  ConflictFootnote,
  ConflictFrame,
  ConflictHeading,
  ConflictRowsList,
  ConflictStrip,
  ReportMetaLine,
} from "./reportStyled";
import { CalloutCard, PanelSecondaryButton } from "./styled";
// the review panel's one dropdown skin — shared with the row editor so a
// name picker reads the same wherever it appears
import { emitAttestationConflict } from "./vero1Telemetry";

/**
 * Flat recommendation list (spec §6.1): the four outcomes, with Lumen's result
 * pre-selected and badged. The preselection uses original_outcome — the
 * canonical mapping of the run's scoreLabelColor — never the
 * client-configurable scoreLabel text (never pattern-match label text).
 * Leaving the preselection alone submits as AGREE (keeps the automated run);
 * anything else is an explicit outcome.
 */
/** Mirrors the server's _RECOMMENDATION_COLOUR — the label candidates and the
 *  derived colour both key on severity, never on the recommendation word. */
const RECOMMENDATION_COLOUR: Partial<
  Record<Vero1Recommendation, Vero1Severity>
> = {
  [Vero1Recommendation.Approve]: "pass",
  [Vero1Recommendation.Conditional]: "warn",
  [Vero1Recommendation.Decline]: "fail",
};

const RECOMMENDATIONS: Array<{
  value: Vero1Recommendation;
  title: string;
  description: string;
  tone: OptionTone;
}> = [
  {
    value: Vero1Recommendation.Approve,
    title: "Approve",
    description: "Passes screening",
    tone: "approve",
  },
  {
    value: Vero1Recommendation.Conditional,
    title: "Conditional",
    description: "Passes with documentation and/or terms",
    tone: "conditional",
  },
  {
    value: Vero1Recommendation.Decline,
    title: "Decline",
    description: "Fails screening",
    tone: "decline",
  },
  {
    value: Vero1Recommendation.Cancel,
    title: "Cancel",
    description: "Recommend cancelling — no verdict",
    tone: "cancel",
  },
];

// RECOMMENDATION_LABELS / CANCEL_REASON_LABELS moved to shared/interfaces/api/vero1
// (single source of truth shared with the deal-summary banner). Imported above.

const CANCEL_REASONS = Object.keys(CANCEL_REASON_LABELS) as Vero1CancelReason[];

/** Spec §11 per-verdict footer lines; the static note covers the rest. */
const FOOTER_NOTES: Partial<Record<Vero1Recommendation, string>> = {
  [Vero1Recommendation.Conditional]:
    "Changed items carry to leasing as written.",
  [Vero1Recommendation.Decline]: "Rationale is required on a decline.",
  // the one option off the verdict axis says what leasing does with it
  // (spec §11.5) — the others describe the submit, this describes the act
  [Vero1Recommendation.Cancel]:
    "Recommends cancelling — leasing cancels the deal.",
};

const isEdited = isLeasingTouched;

/** A moved result reads once, in the client's names (spec 11.2). */
const changeDiff = (item: Vero1ChecklistItem) => {
  // the server's flag also knows a same-severity rename moved the result
  if (!(item.resultMoved ?? hasResultMoved(item))) return null;
  const after =
    displayName(item.overrideOutcomeName) ||
    clientNameFor(item.overrideSeverity ?? item.originalSeverity, item) ||
    severityLabel(item.overrideSeverity);
  const before = clientNameFor(item.originalSeverity, item) ?? NO_VERDICT_LABEL;
  return `${before} → ${after}`;
};

/** What the summary-of-changes line reads for an edited row. */
const summaryLine = (item: Vero1ChecklistItem) => {
  const diff = changeDiff(item);
  if (diff) return diff;
  if (isAnnotationOnlyItem(item)) return `${lumenLabel(item)} · annotated`;
  // the client's name is the result; the severity word is the fallback (AC 4)
  if (item.overrideSeverity != null)
    return (
      clientNameFor(item.overrideSeverity, item) ??
      severityLabel(item.overrideSeverity)
    );
  if (item.originalSeverity != null)
    return `${
      clientNameFor(item.originalSeverity, item) ??
      severityLabel(item.originalSeverity)
    } · annotated`;
  if (item.originalValue !== null && item.originalValue !== undefined)
    return `${formatItemValue(
      item.originalValue,
      item.criterionKey
    )} · annotated`;
  return `${NO_VERDICT_LABEL} · annotated`;
};

/** Mirrors the backend guard: every custom row must be assessed; the server's flag wins. */
const isUnassessedCustom = (item: Vero1ChecklistItem) =>
  item.itemType === Vero1ItemType.Custom &&
  (item.needsAssessment ?? item.overrideSeverity == null);

const isUnassessedOutcome = (item: Vero1ChecklistItem) =>
  item.itemType !== Vero1ItemType.Custom &&
  isAssessable(item) &&
  !isAssessed(item);

/** The outcome a previous review stands for: an agreement is the outcome it
 *  agreed to, so a copy lands on a real option card (PRODUCT-3193). */
const previousOutcome = (
  previous: Vero1Checklist
): Vero1Recommendation | null =>
  previous.finalRecommendation === Vero1Recommendation.Agree
    ? previous.originalOutcome ?? null
    : previous.finalRecommendation;

/** One read-only line of the previous review; a blank field reads "—". */
const PreviousLine = ({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) => (
  <SummaryEntry>
    <span>{label}</span>
    <div>
      <SummaryFindings>{value || "—"}</SummaryFindings>
    </div>
  </SummaryEntry>
);

const listLabels = (rows: Vero1ChecklistItem[]) =>
  rows.map((row) => row.label).join(", ");

const oneOrMany = (rows: unknown[], one: string, many: string) =>
  rows.length === 1 ? one : many;

export interface ReviewReportStepProps {
  dealId: Key;
  checklist: Vero1Checklist;
  onBack: () => void;
  /** Called after a successful submit — the panel closes onto the toast. */
  onSubmitted: () => void;
  /** The panel's centreRow — the one scroll authority for the panel body,
   *  shared with the checklist step. A verdict pick centres the follow-up
   *  fields it reveals, the way an opening row editor is centred. */
  onCentre?: (element: HTMLElement | null) => void;
}

export const ReviewReportStep = ({
  dealId,
  checklist,
  onBack,
  onSubmitted,
  onCentre,
}: ReviewReportStepProps) => {
  const { submitChecklist, isSubmittingChecklist } =
    useSubmitVero1Checklist(dealId);

  // attestation token (spec 6.6): pinned when the report opens —
  // a checklist that moves after this point rejects the submit so the agent
  // chooses
  const [attestToken, setAttestToken] = useState(checklist.updatedAt);
  const [submitConflict, setSubmitConflict] = useState(false);
  // a 409 already invalidated the query; Reload must pin the REFRESHED
  // version, so it waits for updatedAt to actually move before re-arming
  const [reloadPending, setReloadPending] = useState(false);
  useEffect(() => {
    if (reloadPending && checklist.updatedAt !== attestToken) {
      setAttestToken(checklist.updatedAt);
      setSubmitConflict(false);
      setReloadPending(false);
    }
  }, [reloadPending, checklist.updatedAt, attestToken]);
  // draft-level autosave (report fields persist as you type, like items);
  // module-scoped queue so leaving the step never kills an in-flight save
  const reportQueue = getVero1ReportAutosaveQueue(checklist.id);
  reportQueue.dealIdRef.current = dealId;
  const [recommendation, setRecommendation] =
    useState<Vero1Recommendation | null>(
      checklist.finalRecommendation ?? checklist.originalOutcome ?? null
    );
  // the client's word for the pick. Lumen's own option opens on the run's
  // label; any other option opens empty so the agent names it deliberately
  const [recommendationLabel, setRecommendationLabel] = useState(
    checklist.finalRecommendationLabel ||
      ((checklist.finalRecommendation ?? checklist.originalOutcome) ===
      checklist.originalOutcome
        ? checklist.originalRecommendation ?? ""
        : "")
  );
  // any agreement prefills the synthesis (design prototype — supersedes the
  // 08-17 approve-only rule); an explicit override clears it
  // MVP ships no derived rationale (design call, 2026-08-20): VERO1 paste
  // property-specific templated language, so generated text is as often
  // deleted as kept. Revisit derivation as a follow-on.
  const [rationale, setRationale] = useState(checklist.decisionRationale ?? "");
  // once the agent types, option switches stop rewriting their words —
  // restored text counts as typed
  const [rationaleTouched, setRationaleTouched] = useState(
    Boolean(checklist.decisionRationale)
  );
  const [cancelReason, setCancelReason] = useState<Vero1CancelReason | "">(
    (checklist.cancelReason as Vero1CancelReason | "") ?? ""
  );
  const [instructionLine, setInstructionLine] = useState(
    checklist.instructionLine ?? ""
  );
  const [clientFindings, setClientFindings] = useState(
    checklist.clientFindings ?? ""
  );

  // a re-review opens blank; the previous review shows read-only above the
  // options, and Copy over is the agent's choice to start from it
  const { versionDetail: previousReview } = useVero1ChecklistVersion(
    dealId,
    checklist.predecessorVersion ?? null,
    checklist.predecessorVersion != null
  );
  // the outcome the previous review stands for; null hides the card
  const previousRec =
    previousReview && previousReview.status !== Vero1ChecklistStatus.Invalidated
      ? previousOutcome(previousReview)
      : null;
  const previous = previousRec ? previousReview : null;
  // set by Copy over; the next submit waits for that save, queued or in flight
  const copySavePending = useRef(false);
  const [waitingOnCopy, setWaitingOnCopy] = useState(false);

  const reportHydrated = useRef(false);
  useEffect(() => {
    if (!reportHydrated.current) {
      reportHydrated.current = true;
      return;
    }
    reportQueue.engine.stage(REPORT_ENTITY, {
      finalRecommendation: recommendation,
      finalRecommendationLabel: recommendationLabel,
      cancelReason,
      instructionLine,
      decisionRationale: rationale,
      clientFindings,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    recommendation,
    recommendationLabel,
    cancelReason,
    instructionLine,
    rationale,
    clientFindings,
  ]);

  const items = checklist.items ?? [];
  const editedItems = items.filter(isEdited);
  // any custom row breaks the one-to-one with Lumen — template-seeded
  // included, since it was configured onto this checklist to be reviewed
  const customItems = items.filter(
    (item) => item.itemType === Vero1ItemType.Custom
  );
  const unassessedCustom = items.filter(isUnassessedCustom);
  const assessable = items.filter(isAssessable);
  const assessedCount = assessable.filter(isAssessed).length;
  const unassessedOutcomes = items.filter(isUnassessedOutcome);
  // a row can move under an open report, so the submit gate's 422 rows are named here
  const incompleteItems = items.filter(isIncomplete);

  // leaving the preselection alone = agreeing = AGREE (keeps the automated
  // run). Custom rows break the one-to-one with Lumen, so the same selection
  // submits as an explicit outcome instead — which also derives a run.
  const agreesWithLumen =
    recommendation !== null && recommendation === checklist.originalOutcome;

  // candidates are keyed by severity, not by the recommendation word
  const labelCandidatesFor = (option: Vero1Recommendation): string[] => {
    const colour = RECOMMENDATION_COLOUR[option];
    if (!colour) return [];
    return checklist.recommendationLabelCandidates?.[colour] ?? [];
  };
  const labelOptions =
    recommendation !== null ? labelCandidatesFor(recommendation) : [];
  // one configured name IS the pick — the option card wears it and no picker
  // renders, so nothing would ever set it; without this the analyst submits an
  // empty label and leasing falls back to Vero's enum, showing a different
  // word from the one they were shown
  const chosenLabel = labelOptions.includes(recommendationLabel)
    ? recommendationLabel
    : labelOptions.length === 1
    ? labelOptions[0]
    : "";
  // the server's flag when it speaks for this pick; recomputed locally while
  // the agent is still switching options ahead of the next autosave read
  const needsRecommendationLabel =
    labelOptions.length > 1 && chosenLabel === "";
  // a label different from the run's own is an override, like a moved row
  const labelMoves =
    agreesWithLumen &&
    chosenLabel !== "" &&
    Boolean(checklist.originalRecommendation) &&
    chosenLabel !== checklist.originalRecommendation;
  const submitsAsAgree = agreesWithLumen && customItems.length === 0;

  // required on Conditional and Decline regardless of agreement, and on any
  // override; optional only on Cancel and an approve agreement (spec 6)
  const needsRationale =
    recommendation === Vero1Recommendation.Conditional ||
    recommendation === Vero1Recommendation.Decline ||
    (recommendation === Vero1Recommendation.Approve && !submitsAsAgree) ||
    labelMoves;

  const customGuardActive =
    recommendation !== null &&
    recommendation !== Vero1Recommendation.Cancel &&
    unassessedCustom.length > 0;

  const needsCancelReason =
    recommendation === Vero1Recommendation.Cancel && !cancelReason;

  const canSubmit =
    recommendation !== null &&
    (!needsRationale || rationale.trim().length > 0) &&
    !needsCancelReason &&
    !customGuardActive &&
    !needsRecommendationLabel &&
    incompleteItems.length === 0;

  // focusing a field below the fold centres it, the way the checklist
  // centres an opening editor — the browser's own focus scroll only nudges
  const centreField = (event: React.FocusEvent<HTMLElement>) => {
    const target = event.currentTarget;
    if (!onCentre || typeof requestAnimationFrame !== "function") return;
    requestAnimationFrame(() => onCentre(target));
  };

  const pickRecommendation = (value: Vero1Recommendation) => {
    setRecommendation(value);
    // the instruction belongs to Conditional — a hidden leftover must not
    // ride along on another recommendation
    if (value !== Vero1Recommendation.Conditional) setInstructionLine("");
    if (value !== Vero1Recommendation.Cancel) setCancelReason("");
    // the label belongs to the option it was picked under; Lumen's own option
    // re-seeds with the run's word, any other opens empty
    setRecommendationLabel(
      value === checklist.originalOutcome
        ? checklist.originalRecommendation ?? ""
        : ""
    );
    // nothing to re-seed while derivation is out — an untouched field stays
    // empty whichever outcome is picked
    if (!rationaleTouched) setRationale("");
    // centre what the agent just picked, not the fields below it: centring a
    // later section drives the chosen option off the top of the body, where it
    // is clipped. After paint, because the pick's follow-up mounts in this
    // same commit and changes the option's position.
    if (onCentre && typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => {
        onCentre(
          document.querySelector<HTMLElement>(
            `[data-testid="vero1-rec-${value.toLowerCase()}"]`
          )
        );
      });
    }
  };

  // Copy over: every field at once, set directly rather than through
  // pickRecommendation so the copied text is not wiped by the pick. A label
  // this review no longer offers is left for the agent to pick again; the
  // outcome then reads as agreement or override against the new screening
  // exactly as a hand pick would.
  const copyPrevious = () => {
    if (!previous || !previousRec) return;
    const next = previousRec;
    const isAgree = previous.finalRecommendation === Vero1Recommendation.Agree;
    // stored labels are prefix-stripped, the run's word is raw: compare like
    // with like, as the backend's label_moved does
    const previousRunWord =
      stripPriorityPrefix(previous.originalRecommendation ?? "") ?? "";
    // an agreement whose label differs from its run's own word carries the
    // agent's deliberate pick; it copies like any label
    const movedOnAgree =
      isAgree &&
      Boolean(previous.finalRecommendationLabel) &&
      stripPriorityPrefix(previous.finalRecommendationLabel) !==
        previousRunWord;
    // an unmoved agreement that still matches agrees with THIS run, so it
    // takes this run's word as a hand pick of Lumen's option does; carrying
    // the old run's word would read as a label override
    const previousLabel =
      !isAgree || movedOnAgree
        ? previous.finalRecommendationLabel ?? ""
        : next === checklist.originalOutcome
        ? checklist.originalRecommendation ?? ""
        : previousRunWord;
    const copied = {
      finalRecommendation: next,
      finalRecommendationLabel: labelCandidatesFor(next).includes(previousLabel)
        ? previousLabel
        : "",
      cancelReason:
        next === Vero1Recommendation.Cancel
          ? ((previous.cancelReason || "") as Vero1CancelReason | "")
          : ("" as const),
      instructionLine:
        next === Vero1Recommendation.Conditional
          ? previous.instructionLine ?? ""
          : "",
      decisionRationale: previous.decisionRationale ?? "",
      clientFindings: previous.clientFindings ?? "",
    };
    setRecommendation(copied.finalRecommendation);
    setRecommendationLabel(copied.finalRecommendationLabel);
    setCancelReason(copied.cancelReason);
    setInstructionLine(copied.instructionLine);
    setRationale(copied.decisionRationale);
    setRationaleTouched(true);
    setClientFindings(copied.clientFindings);
    // the copied values and their source version go in one save — the server
    // records the copy only when the values ride with it, and a re-copy of
    // values already in the fields changes no state to restage them
    reportQueue.engine.stage(REPORT_ENTITY, {
      ...copied,
      copiedFromVersion: previous.version,
    });
    copySavePending.current = true;
  };

  // rows that moved after the report opened — what the conflict banner names
  const movedRows = items.filter(
    (item) =>
      item.lastEditedAt && new Date(item.lastEditedAt) > new Date(attestToken)
  );

  const submitNow = (options?: { anyway?: boolean }) =>
    submitChecklist(
      {
        finalRecommendation: submitsAsAgree
          ? Vero1Recommendation.Agree
          : (recommendation as Vero1Recommendation),
        finalRecommendationLabel: chosenLabel,
        decisionRationale: rationale,
        instructionLine,
        clientFindings,
        cancelReason:
          recommendation === Vero1Recommendation.Cancel ? cancelReason : "",
        expectedUpdatedAt: options?.anyway ? null : attestToken,
      },
      {
        onSuccess: () => {
          disposeVero1ReportAutosaveQueue(checklist.id);
          onSubmitted();
        },
        onError: (error: {
          statusCode?: number;
          response?: { status?: number };
        }) => {
          const status = error?.statusCode ?? error?.response?.status;
          if (status === 409) {
            setSubmitConflict(true);
            emitAttestationConflict({
              conflictCode: codeOf(error),
              dealId,
              checklistId: checklist.id,
            });
          }
        },
      }
    );

  // the deferred submit sends what the fields hold when the wait ends, not
  // what they held at the click
  const submitNowRef = useRef(submitNow);
  submitNowRef.current = submitNow;

  // a Copy over's save goes first — still debounced or already in flight —
  // so its Activity entry lands before the review stops being a draft. The
  // buttons hold until then, so a second click can't send a submit beside it
  const submit = (options?: { anyway?: boolean }) => {
    if (waitingOnCopy) return;
    if (copySavePending.current) {
      copySavePending.current = false;
      setWaitingOnCopy(true);
      reportQueue.engine.flush(REPORT_ENTITY).finally(() => {
        setWaitingOnCopy(false);
        submitNowRef.current(options);
      });
      return;
    }
    submitNow(options);
  };

  // spec 6.6: the checklist moved between opening the report and submitting.
  // Everything typed is kept; the agent decides informed vs regardless.
  const editTime = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        })
      : "";

  const conflictBanner = submitConflict && (
    <ConflictFrame data-testid="vero1-submit-conflict">
      <ConflictStrip>
        <CircleAlertGlyph />
        <div>
          <ConflictHeading>
            Not submitted — the checklist changed while you were writing
          </ConflictHeading>
          <ConflictCopy>
            {movedRows.length === 1
              ? "A row was"
              : `${movedRows.length || "Some"} rows were`}{" "}
            saved by someone else after you opened this review. Your
            recommendation and rationale are untouched below.
          </ConflictCopy>
        </div>
      </ConflictStrip>
      {movedRows.length > 0 && (
        <ConflictRowsList>
          {movedRows.map((item) => (
            <FlexContainer
              key={item.id}
              alignItems="center"
              justifyContent="space-between"
              gap="9px"
            >
              <SmallText>{item.label}</SmallText>
              <SmallText weak>
                {item.lastEditedByName ? `${item.lastEditedByName} · ` : ""}
                {editTime(item.lastEditedAt)}
              </SmallText>
            </FlexContainer>
          ))}
        </ConflictRowsList>
      )}
      <ConflictActionsBar>
        <ConflictButtonRow>
          <Button
            type="secondary"
            data-testid="vero1-reload-checklist"
            loading={reloadPending}
            onClick={() => {
              if (checklist.updatedAt !== attestToken) {
                setAttestToken(checklist.updatedAt);
                setSubmitConflict(false);
              } else {
                setReloadPending(true);
              }
            }}
          >
            Reload checklist
          </Button>
          <Button
            type="secondary"
            data-testid="vero1-submit-anyway"
            loading={isSubmittingChecklist || waitingOnCopy}
            onClick={() => submit({ anyway: true })}
          >
            Submit anyway
          </Button>
        </ConflictButtonRow>
        <ConflictFootnote>
          Reloading keeps your recommendation and rationale — it only refreshes
          the rows above.
        </ConflictFootnote>
      </ConflictActionsBar>
    </ConflictFrame>
  );

  const previousName = previous && resolveVero1RecommendationName(previous);

  const previousCard = previous && previousRec && (
    <ReportCard data-testid="vero1-previous-review">
      <ReportCardHeader>
        <FlexContainer alignItems="center" gap="8px">
          Previous review
          <CountChip>v{previous.version}</CountChip>
        </FlexContainer>
      </ReportCardHeader>
      <ReportCardBody>
        <SummaryEntry>
          <span>Recommendation</span>
          <div>
            <SummaryResult
              $tone={previous.effectiveRecommendationColour ?? null}
            >
              {RECOMMENDATION_LABELS[previousRec]}
              {previous.finalRecommendation === Vero1Recommendation.Agree &&
                " · agreed with Lumen"}
            </SummaryResult>
          </div>
        </SummaryEntry>
        {previousName && (
          <PreviousLine
            label="Leasing will read"
            value={displayName(previousName)}
          />
        )}
        {previous.finalRecommendation === Vero1Recommendation.Cancel && (
          <PreviousLine
            label="Why the process ended"
            value={
              previous.cancelReason
                ? CANCEL_REASON_LABELS[previous.cancelReason]
                : ""
            }
          />
        )}
        <PreviousLine
          label="Why this recommendation"
          value={previous.decisionRationale}
        />
        <PreviousLine
          label="Findings for the client"
          value={previous.clientFindings}
        />
        {/* the copied outcome, so an agreement with a Conditional shows the
            instruction Copy over will paste */}
        {previousRec === Vero1Recommendation.Conditional && (
          <PreviousLine
            label="Instruction to leasing"
            value={previous.instructionLine}
          />
        )}
        <SummaryCardFoot>
          <span>
            Copies these into the fields below. They were written for v
            {previous.version} — review them before submitting.
          </span>
          <PanelSecondaryButton
            data-testid="vero1-copy-previous"
            onClick={copyPrevious}
          >
            Copy over
          </PanelSecondaryButton>
        </SummaryCardFoot>
      </ReportCardBody>
    </ReportCard>
  );

  const summaryOfChanges = (
    <ReportCard>
      <ReportCardHeader>
        <FlexContainer alignItems="center" gap="8px">
          Summary of changes
          <CountChip>
            {editedItems.length} {editedItems.length === 1 ? "item" : "items"}
          </CountChip>
        </FlexContainer>
      </ReportCardHeader>
      <ReportCardBody>
        {editedItems.length === 0 ? (
          <SmallText weak data-testid="vero1-summary-empty">
            No item was changed — the review agrees with screening on every row.
          </SmallText>
        ) : (
          <div data-testid="vero1-summary-of-changes">
            {editedItems.map((item) => (
              // canvas 11.2: the item name holds a fixed gutter; its result
              // and findings stack in the column beside it
              <SummaryEntry key={item.id}>
                <span>{item.label}</span>
                <div>
                  <SummaryResult $tone={effectiveTone(item)}>
                    {summaryLine(item)}
                  </SummaryResult>
                  {item.overrideExplanation && (
                    <SummaryFindings>
                      {item.overrideExplanation}
                    </SummaryFindings>
                  )}
                  {item.actionForLeasing && (
                    <SummaryAction>
                      <SummaryFieldLabel>Leasing action</SummaryFieldLabel>
                      <span>{item.actionForLeasing}</span>
                    </SummaryAction>
                  )}
                </div>
              </SummaryEntry>
            ))}
          </div>
        )}
        <SummaryCardFoot>
          <span>Edit an item to change what appears here.</span>
          <BackLink
            type="button"
            data-testid="vero1-summary-back-to-checklist"
            onClick={onBack}
          >
            Back to checklist
          </BackLink>
        </SummaryCardFoot>
      </ReportCardBody>
    </ReportCard>
  );

  return (
    <div data-testid="vero1-report-step">
      {/* the option menu renders in a portal, outside this subtree */}
      <SelectFieldMenuStyles />
      <BackLink type="button" onClick={onBack}>
        ‹ Back to checklist
      </BackLink>
      <Spacer size={Spacer.SIZES.sm} />
      <ReportMetaLine data-testid="vero1-assessed-count">
        {assessedCount} of {assessable.length}{" "}
        {oneOrMany(assessable, "item", "items")} assessed · {editedItems.length}{" "}
        edited
      </ReportMetaLine>

      {conflictBanner}

      {previousCard}

      <SectionLabel>
        Deal recommendation <RequiredMark>*</RequiredMark>
      </SectionLabel>
      {!checklist.originalOutcome && (
        <SmallText
          weak
          data-testid="vero1-no-preselection"
          style={{ display: "block", margin: "2px 0 8px" }}
        >
          {checklist.originalRecommendation
            ? `Lumen's result — "${checklist.originalRecommendation}" — has no recognized severity, so nothing is pre-selected. Your pick submits as an explicit outcome.`
            : "Lumen produced no recommendation on the pinned run — pick an explicit outcome."}
        </SmallText>
      )}
      {RECOMMENDATIONS.map((option) => {
        const isLumens = option.value === checklist.originalOutcome;
        const isPicked = recommendation === option.value;
        const optionLabels = labelCandidatesFor(option.value);
        const showLabelTray = isPicked && optionLabels.length > 1;
        // the option wears the word leasing will read: the sole configured
        // name, or — once the agent has picked from several — their choice.
        // Falls back to Vero's own word only while nothing is settled.
        const optionWord =
          optionLabels.length === 1
            ? displayName(optionLabels[0])
            : isPicked && chosenLabel
            ? displayName(chosenLabel)
            : option.title;
        return (
          <React.Fragment key={option.value}>
            <OptionCard
              type="button"
              $selected={recommendation === option.value}
              $attached={showLabelTray}
              $tone={option.tone}
              data-testid={`vero1-rec-${option.value.toLowerCase()}`}
              onClick={() => pickRecommendation(option.value)}
            >
              <OptionMain>
                <OptionRadio
                  $selected={recommendation === option.value}
                  $tone={option.tone}
                />
                <OptionRow>
                  <OptionWord
                    $selected={recommendation === option.value}
                    $tone={option.tone}
                  >
                    {optionWord}
                  </OptionWord>
                  <OptionDescription>{option.description}</OptionDescription>
                  {isLumens && (
                    // inline at the row's end per the spec drawing; Lumen's own
                    // wording rides the tooltip rather than lengthening the row
                    <LumenResultBadge
                      data-testid="vero1-lumen-result-badge"
                      title={
                        checklist.originalRecommendation
                          ? `Lumen's label: "${checklist.originalRecommendation}"`
                          : undefined
                      }
                    >
                      Lumen&apos;s result
                    </LumenResultBadge>
                  )}
                </OptionRow>
              </OptionMain>
            </OptionCard>
            {showLabelTray && (
              <OptionLabelPicker
                $tone={option.tone}
                data-testid={`vero1-rec-label-${option.value.toLowerCase()}`}
              >
                <OptionLabelHeading $tone={option.tone}>
                  Leasing will read *
                </OptionLabelHeading>
                <SelectField>
                  {/* @ts-ignore — Select is untyped JS */}
                  <Select
                    dropdownClassName={VERO1_DROPDOWN_CLASS}
                    id="vero1-recommendation-label"
                    value={chosenLabel || undefined}
                    onChange={(next) =>
                      setRecommendationLabel(String(next ?? ""))
                    }
                    placeholder="Select a name"
                  >
                    {labelOptions.map((label) => (
                      // @ts-ignore
                      <Select.Option key={label} value={label}>
                        {displayName(label)}
                      </Select.Option>
                    ))}
                  </Select>
                </SelectField>
              </OptionLabelPicker>
            )}
          </React.Fragment>
        );
      })}
      {agreesWithLumen && customItems.length > 0 && (
        <SmallText weak data-testid="vero1-agree-custom-note">
          This review has items Lumen didn&apos;t evaluate, so the review
          submits as an explicit {RECOMMENDATION_LABELS[recommendation!]} rather
          than an as-is agreement.
        </SmallText>
      )}

      {recommendation === Vero1Recommendation.Cancel && (
        <>
          <SectionLabel>
            Why the process ended <RequiredMark>*</RequiredMark>
          </SectionLabel>
          <ReasonChipRow role="radiogroup" data-testid="vero1-cancel-reason">
            {CANCEL_REASONS.map((reason) => (
              <ReasonChip
                key={reason}
                $selected={cancelReason === reason}
                data-label={CANCEL_REASON_LABELS[reason]}
              >
                <input
                  type="radio"
                  name="vero1-cancel-reason"
                  data-testid={`vero1-cancel-${reason.toLowerCase()}`}
                  checked={cancelReason === reason}
                  onChange={() => setCancelReason(reason)}
                />
                {CANCEL_REASON_LABELS[reason]}
              </ReasonChip>
            ))}
          </ReasonChipRow>
        </>
      )}

      <>
        <SectionLabel>
          Why this recommendation {needsRationale ? "*" : "— Optional"}
        </SectionLabel>
        <TextArea
          aria-label="Why this recommendation"
          onFocus={centreField}
          value={rationale}
          onChange={(event) => {
            setRationale(event.target.value);
            setRationaleTouched(true);
          }}
          placeholder="Reference the line items driving the call — leasing reads this verbatim."
        />
      </>

      <SectionLabel>Findings for the client — Optional</SectionLabel>
      <TextArea
        aria-label="Findings for the client"
        onFocus={centreField}
        value={clientFindings}
        onChange={(event) => setClientFindings(event.target.value)}
        placeholder="Deal-level narrative the client reads — context that spans items. Per-item findings stay on their rows."
      />

      {recommendation === Vero1Recommendation.Conditional && (
        <>
          <SectionLabel>Instruction to leasing — Optional</SectionLabel>
          <TextInput
            aria-label="Instruction to leasing"
            onFocus={centreField}
            maxLength={255}
            value={instructionLine}
            onChange={(event) => setInstructionLine(event.target.value)}
            placeholder="e.g. Awaiting applicant income documents"
          />
        </>
      )}

      {summaryOfChanges}

      {customGuardActive && (
        <CalloutCard $role="guard" data-testid="vero1-custom-guard">
          <SmallText>
            These items must be assessed before submitting:{" "}
            <strong>
              {unassessedCustom.map((item) => item.label).join(", ")}
            </strong>
          </SmallText>
        </CalloutCard>
      )}

      <ReportFooter>
        <ReportFooterNote data-testid="vero1-report-footer-note">
          {incompleteItems.length > 0
            ? `${
                incompleteItems.some(needsName)
                  ? "Needs a name, reason or findings"
                  : incompleteItems.some(needsReason)
                  ? "Needs a reason or findings"
                  : "Needs findings"
              } before submitting: ${listLabels(incompleteItems)}.`
            : needsRecommendationLabel
            ? "Pick the name leasing should read for this recommendation before submitting."
            : [
                unassessedOutcomes.length > 0 &&
                  `No verdict on ${listLabels(
                    unassessedOutcomes
                  )} — leasing sees ${oneOrMany(
                    unassessedOutcomes,
                    "it",
                    "them"
                  )} as Lumen left ${oneOrMany(
                    unassessedOutcomes,
                    "it",
                    "them"
                  )}.`,
                (recommendation && FOOTER_NOTES[recommendation]) ||
                  "Nothing reaches leasing until you submit.",
              ]
                .filter(Boolean)
                .join(" ")}
        </ReportFooterNote>
        <SubmitButton
          type="primary"
          loading={isSubmittingChecklist || waitingOnCopy}
          disabled={!canSubmit || submitConflict}
          onClick={() => submit()}
        >
          Submit review
        </SubmitButton>
      </ReportFooter>
    </div>
  );
};
